/**
 * «Avance de obra» en el enlace PÚBLICO para el cliente (idea 9 + idea 11): el video de cómo va la obra sobre el modelo o el
 * dibujo, con la curva en porcentaje y la lista de conceptos. Solo lectura, sin sesión: todo sale de la foto congelada del
 * enlace (`AvancePublico`: fracciones, fechas y piezas del propio archivo). Sin dinero, sin nombres de obra de otros sistemas y
 * sin jerga: los textos están en `TEXTOS_AVANCE_CLIENTE` (los revisa una prueba).
 */
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Spinner } from '@fluentui/react-components';
import {
  avanceDePiezasEn,
  diaDeFecha,
  interpolar,
  nudosDeAvance,
  type AvancePublico,
  type ConceptoParaPintar,
  type RenglonPublico,
} from '@supernova/shared';
import {
  etiquetaDeDia,
  mapaParaElVisor,
  type CajaDePieza,
} from '../../lib/avanceObra4d.js';
import {
  hayPorcentajeDeObra,
  huellasDelDibujo,
  nudosDeConcepto,
  periodosParaCurva,
  pintablesDwg,
  pintablesIfc,
} from '../../lib/avancePublico.js';
import type { CensoDelPlano } from '../PlanoViewerMlightcad.js';
import { BarraDeReproduccion } from './BarraDeReproduccion.js';
import { CurvaS } from './CurvaS.js';
import { useReproduccionDeAvance } from './useReproduccionDeAvance.js';

const PlanoViewerMlightcad = lazy(() =>
  import('../PlanoViewerMlightcad.js').then((m) => ({ default: m.PlanoViewerMlightcad })),
);
const IfcViewer3D = lazy(() => import('../IfcViewer3D.js').then((m) => ({ default: m.IfcViewer3D })));

/** Todo lo que el cliente lee en esta pantalla (sin jerga del sistema). */
export const TEXTOS_AVANCE_CLIENTE = {
  titulo: 'Avance de obra',
  curva: 'Cómo ha avanzado la obra',
  conceptos: 'Conceptos de este plano',
  sinConceptos: 'Los conceptos de este plano todavía no tienen avance.',
  leyenda: 'Terminado · En proceso · Por construir',
  sinPiezas: 'Este plano no muestra el avance pieza por pieza; el porcentaje y la curva sí.',
  deToda: 'de toda la obra',
} as const;

const COLORES_2D = { added: 0x2ea043, modified: 0x3b82f6, deleted: 0xff2bd6, base: 0x6b737c };
const SIN_RENGLONES: never[] = [];
const SIN_RESALTE = { capas: [] as string[], bloques: [] as string[], rol: 'added' as const };
const NADA = () => undefined;
const pct = (f: number): string => `${Math.round(f * 100)} %`;

interface Props {
  avance: AvancePublico;
  renglones: readonly RenglonPublico[];
  formato: 'dxf' | 'ifc' | null;
  /** IFC: URL local (blob) del modelo; null mientras se baja. */
  ifcUrl: string | null;
  errorIfc: string | null;
  cargarDxf: () => Promise<ArrayBuffer>;
}

export function AvanceParaElCliente({ avance, renglones, formato, ifcUrl, errorIfc, cargarDxf }: Props) {
  const esDwg = formato === 'dxf';
  const periodos = useMemo(() => periodosParaCurva(avance), [avance]);
  const rangoReal = useMemo(() => {
    let desde = Infinity;
    for (const p of avance.periodos) desde = Math.min(desde, diaDeFecha(p.desde));
    return { desde, hasta: diaDeFecha(avance.hasta) };
  }, [avance]);
  const { t, reproduciendo, velocidad, setVelocidad, saltarA, alternar } = useReproduccionDeAvance(rangoReal);

  const [cajas, setCajas] = useState<ReadonlyMap<number, CajaDePieza> | null>(null);
  const [censo, setCenso] = useState<CensoDelPlano | null>(null);
  const [pintablesDelDibujo, setPintablesDelDibujo] = useState<ConceptoParaPintar[] | null>(null);

  // DWG: las huellas de la foto se emparejan con los nombres del dibujo (asíncrono: sha-256).
  useEffect(() => {
    if (!esDwg || !censo) return;
    let vivo = true;
    huellasDelDibujo(censo)
      .then((h) => vivo && setPintablesDelDibujo(pintablesDwg(avance, censo, h)))
      .catch(() => vivo && setPintablesDelDibujo([]));
    return () => {
      vivo = false;
    };
  }, [esDwg, censo, avance]);

  const sinPiezas = avance.modo === 'sin_piezas' || formato === null;
  const pintables = useMemo<ConceptoParaPintar[]>(() => {
    if (sinPiezas) return [];
    if (esDwg) return pintablesDelDibujo ?? [];
    return cajas ? pintablesIfc(avance, cajas) : [];
  }, [sinPiezas, esDwg, pintablesDelDibujo, cajas, avance]);
  const listo = sinPiezas || (esDwg ? pintablesDelDibujo !== null : cajas !== null);

  const estadoDePiezas = useMemo(() => (t != null ? avanceDePiezasEn(pintables, t) : new Map()), [pintables, t]);
  const avance4d = useMemo(() => (t != null && cajas ? mapaParaElVisor(estadoDePiezas) : null), [estadoDePiezas, cajas, t]);
  const dibujoCompleto = useMemo(() => {
    if (!esDwg || estadoDePiezas.size === 0) return false;
    for (const x of estadoDePiezas.values()) if (x.estado !== 'terminada') return false;
    return true;
  }, [esDwg, estadoDePiezas]);
  const rolesDwg = useMemo(() => {
    if (!esDwg || dibujoCompleto) return null;
    const m = new Map<string, 'added' | 'deleted' | 'modified'>();
    for (const [id, x] of estadoDePiezas) {
      if (x.estado === 'terminada') m.set(id, 'added');
      else if (x.estado === 'en_proceso') m.set(id, 'modified');
    }
    return m;
  }, [esDwg, dibujoCompleto, estadoDePiezas]);

  const nudosObra = useMemo(
    () => nudosDeAvance(periodos, new Map(periodos.map((p) => [p.numero, p.importeAcumulado] as const))),
    [periodos],
  );
  const conPorcentaje = hayPorcentajeDeObra(avance);
  const pctObra = t != null ? interpolar(nudosObra, t) : 0;

  const porN = useMemo(() => new Map(renglones.map((r) => [r.n, r] as const)), [renglones]);
  const filas = useMemo(
    () =>
      avance.conceptos
        .map((c, i) => {
          const nudos = nudosDeConcepto(avance, c, periodos);
          return { c, r: porN.get(c.renglonN), nudos, i, inicio: nudos.find((n) => n.v > 0)?.t ?? Infinity };
        })
        .filter((f) => f.r !== undefined)
        .sort((a, b) => (a.inicio === b.inicio ? a.i - b.i : a.inicio - b.inicio)),
    [avance, periodos, porN],
  );

  const conteo = useMemo(() => {
    let terminadas = 0, enProceso = 0, pendientes = 0;
    for (const x of estadoDePiezas.values()) {
      if (x.estado === 'terminada') terminadas++;
      else if (x.estado === 'en_proceso') enProceso++;
      else pendientes++;
    }
    return { terminadas, enProceso, pendientes };
  }, [estadoDePiezas]);

  if (t == null) return <Spinner label="Cargando…" />;

  return (
    <div style={{ display: 'grid', rowGap: 12 }}>
      <div className="avance-cliente-rejilla" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 16 }}>
        <style>{`@media (min-width: 900px) { .avance-cliente-rejilla { grid-template-columns: minmax(0, 1fr) 380px !important; } }`}</style>

        {/* El modelo o el dibujo */}
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '60vh',
            minHeight: 320,
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-sm, 4px)',
            overflow: 'hidden',
            background: 'var(--bg-surface)',
          }}
        >
          <Suspense fallback={<Spinner label="Cargando plano…" />}>
            {formato === 'dxf' && (
              <PlanoViewerMlightcad
                projectId=""
                fileId=""
                renglones={SIN_RENGLONES}
                cargarDxf={cargarDxf}
                comparacionPorEntidad={rolesDwg}
                resaltarCambio={dibujoCompleto ? null : SIN_RESALTE}
                coloresComparacion={COLORES_2D}
                onCenso={setCenso}
                onSeleccion={NADA}
                sinControles
              />
            )}
            {formato === 'ifc' &&
              (ifcUrl ? (
                <IfcViewer3D srcUrl={ifcUrl} sinBarraDeEstado avance4d={avance4d} onCajasDePiezas={setCajas} />
              ) : errorIfc ? (
                <p>{errorIfc}</p>
              ) : (
                <Spinner label="Cargando modelo…" />
              ))}
          </Suspense>
          {pintables.length > 0 && (
            <div style={{ position: 'absolute', left: 10, bottom: 10, fontSize: 12, color: 'var(--text-secondary)', background: 'var(--bg-raised)', padding: '4px 8px', borderRadius: 4 }}>
              {TEXTOS_AVANCE_CLIENTE.leyenda}: {conteo.terminadas} · {conteo.enProceso} · {conteo.pendientes}
            </div>
          )}
        </div>

        {/* El panel */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 12, alignContent: 'start', minWidth: 0 }}>
          <div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {TEXTOS_AVANCE_CLIENTE.titulo} al {etiquetaDeDia(t)}
            </div>
            {conPorcentaje && (
              <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1 }}>
                {pct(pctObra)} {TEXTOS_AVANCE_CLIENTE.deToda}
              </div>
            )}
          </div>

          {conPorcentaje && (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>{TEXTOS_AVANCE_CLIENTE.curva}</div>
              <CurvaS
                periodos={periodos}
                desde={rangoReal.desde}
                hasta={rangoReal.hasta}
                t={t}
                contratado={1}
                moneyCorto={pct}
                onElegirFecha={saltarA}
              />
            </div>
          )}

          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{TEXTOS_AVANCE_CLIENTE.conceptos}</div>
            {filas.length === 0 && <div style={{ color: 'var(--text-secondary)' }}>{TEXTOS_AVANCE_CLIENTE.sinConceptos}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', rowGap: 6, maxHeight: 340, overflowY: 'auto', paddingRight: 4 }}>
              {filas.map(({ c, r, nudos }) => {
                const f = Math.max(0, Math.min(1, interpolar(nudos, t)));
                return (
                  <div key={c.renglonN}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }} title={r!.descripcion}>
                        {r!.clave ? `${r!.clave} · ` : ''}
                        {r!.descripcion}
                      </span>
                      <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{pct(f)}</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--border-subtle)', borderRadius: 3, overflow: 'hidden', marginTop: 3 }}>
                      <div style={{ width: `${f * 100}%`, height: '100%', background: 'var(--accent)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {sinPiezas && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{TEXTOS_AVANCE_CLIENTE.sinPiezas}</div>}
        </div>
      </div>

      <BarraDeReproduccion
        rango={rangoReal}
        t={t}
        reproduciendo={reproduciendo}
        velocidad={velocidad}
        deshabilitado={!listo || pintables.length === 0}
        sinVelocidad={typeof window !== 'undefined' && window.innerWidth < 640}
        onAlternar={() => alternar()}
        onElegirFecha={saltarA}
        onVelocidad={setVelocidad}
      />
    </div>
  );
}
